import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import ApiService from '../services/api';
import { UAParser } from 'ua-parser-js';
import { cookieHelpers } from '../utils/cookieUtils';

interface SessionProviderProps {
  children: React.ReactNode;
}

// Function to generate device ID without third-party services
const generateDeviceId = (): string => {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx?.fillText('Device ID', 10, 10);
    const canvasFingerprint = canvas.toDataURL();
    
    const screenInfo = `${screen.width}x${screen.height}x${screen.colorDepth}`;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const language = navigator.language;
    const platform = navigator.platform;
    const userAgent = navigator.userAgent;
    const hardwareConcurrency = navigator.hardwareConcurrency || 'unknown';
    const deviceMemory = (navigator as any).deviceMemory || 'unknown';
    const maxTouchPoints = navigator.maxTouchPoints || 0;
    
    const deviceCharacteristics = [
      canvasFingerprint,
      screenInfo,
      timezone,
      language,
      platform,
      userAgent,
      hardwareConcurrency,
      deviceMemory,
      maxTouchPoints,
      window.devicePixelRatio || 1,
      window.innerWidth,
      window.innerHeight,
      new Date().getTimezoneOffset(),
      navigator.cookieEnabled ? '1' : '0',
      navigator.onLine ? '1' : '0',
      navigator.doNotTrack || 'unknown'
    ].join('|');
    
    let hash = 0;
    for (let i = 0; i < deviceCharacteristics.length; i++) {
      const char = deviceCharacteristics.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    
    const deviceId = Math.abs(hash).toString(16).padStart(8, '0');
    
    return `device_${deviceId}`;
  } catch (error) {
    console.error('Error generating device ID:', error);
    return `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
};
export const createSession = async (setSessionId: (id: string) => void, setSessionError: (error: string | null) => void, language: string = 'ENG', userId?: string, setRole?: (role: 1 | 2) => void, distributorIdFromSignup?: string, distributorId?: string) => {
  try {

    const parser = new UAParser();
    const result = parser.getResult();
    
    const browser = result.browser.name || 'Unknown';
    const platform = result.os.name || 'Unknown';
    
    let device = 'Unknown';
    if (result.device.type) {
      device = result.device.type;
    } else if (result.device.vendor) {
      device = result.device.vendor;
    } else if (result.device.model) {
      device = result.device.model;
    } else {
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const isTablet = /iPad|Android(?=.*\bMobile\b)(?=.*\bSafari\b)/i.test(navigator.userAgent);
      
      if (isTablet) {
        device = 'tablet';
      } else if (isMobile) {
        device = 'mobile';
      } else {
        device = 'desktop';
      }
    }
    
  

    let deviceId: string;

    deviceId = generateDeviceId();
    

    // Determine flow type from URL path
    const apiService = new ApiService();
    const flowType = apiService.getFlowTypeFromUrl();


    // Set role globally in context
    const roleNumber = flowType === 'distributor' ? 1 : 2;
    if (setRole) {
      setRole(roleNumber);
    }
    
    const newSessionId = await apiService.createSession(
      device, 
      browser, 
      platform,  
      deviceId, 
      language, 
      userId,       // Pass userId if provided
      roleNumber,   // Pass the role number
      distributorIdFromSignup,
      distributorId // Pass distributorId from signup if provided
    );
    
    setSessionId(newSessionId);
    setSessionError(null);
    
    return newSessionId;
    
  } catch (error: any) {
    const errorMessage = error.message || 'Failed to create session. Please try again.';
    setSessionError(errorMessage);
    console.error('Session creation failed:', errorMessage);
    throw error;
  }
};

export const SessionProvider: React.FC<SessionProviderProps> = ({ children }) => {
  const { sessionId, setSessionId, setSessionError, setCurrentView, userId, setRole, distributorId, flowType, currentLanguage } = useAppContext();
  const [isInitializing, setIsInitializing] = useState(true);
  const location = useLocation();
  const hasInitialized = useRef(false);

  useEffect(() => {
    const initializeSession = async () => {
      // Don't create a new session if one already exists or if we've already initialized
      if (sessionId || hasInitialized.current) {
        setIsInitializing(false);
        return;
      }

      try {
        hasInitialized.current = true;
        setIsInitializing(true);
        setSessionError(null);

        const language = currentLanguage || 'en';
        await createSession(setSessionId, setSessionError, language, userId || undefined, setRole, distributorId || undefined);
        
        // Set appropriate initial view based on URL path
        const storedToken = cookieHelpers.getToken(flowType);
        if (location.pathname === "" || location.pathname === "/" || location.pathname.startsWith('/distributor')) {
          // Check if there's a valid token for distributor flow
          if (storedToken) {
            // Token exists, set to welcome page directly (AppContext will validate and redirect if needed)
            setCurrentView('distributor-welcome');
          } else {
            // No token, go to login
            setCurrentView('distributor-login');
          }
        } else {
          if (storedToken) {
            setCurrentView('Welcome');
          } else {
            setCurrentView('home');
          }
        }
        
      } catch (error: any) {
        // Set error view
        setCurrentView('error');
      } finally {
        setIsInitializing(false);
      }
    };

    initializeSession();
  }, [location.pathname]);

  // Show loading state while initializing session
  if (isInitializing) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        fontSize: '1.2rem',
        color: 'var(--color-text-secondary)'
      }}>
        Loading...
      </div>
    );
  }

  return <>{children}</>;
}; 